/**
 * Step-3 view: live tracking of the import job the worker is running.
 *
 * After the CSV is PUT to S3 and handed off via `rescueStartImport`, the
 * wizard polls `rescueCsvImportProgress` (see `useImportProgress`) and
 * renders one of three states here:
 *
 *   in flight (starting / tracking) — live phase heading, a determinate
 *                                     bar (APPLYING) or spinner, running
 *                                     row counts, and a recent-error sample
 *   succeeded                       — check hero + final counts
 *   failed (errored)                — error hero + reason + retry path
 *
 * Unlike the old static "upload received" panel, this reflects the
 * actual outcome of the server-side import rather than just the upload.
 */

import React from 'react';
import Button from '../../../components/Button/Button';
import { ModalActions } from '../../../components/Modal/Modal';
import type { ImportProgressPhase } from '../../../hooks/useImportProgress';
import type { RescueCsvImportProgress } from '../../../lib/integrationQueries';
import { CheckIcon, ErrorCircleIcon } from './icons';
import styles from '../Integrations.module.scss';

interface ImportProgressProps {
  phase: ImportProgressPhase;
  progress: RescueCsvImportProgress | null;
  errorMessage: string | null;
  pollError: boolean;
  fileName: string | null;
  onClose: () => void;
  onUploadAnother: () => void;
}

function Spinner() {
  return (
    <svg
      className={styles.progressSpinner}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" opacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

/** A labelled count tile, mirroring the ShelterLuv success-stat styling. */
function Stat({ value, label, danger }: { value: number; label: string; danger?: boolean }) {
  return (
    <div className={styles.successStat}>
      <div className={`${styles.successStatValue}${danger ? ` ${styles.successStatValueDanger}` : ''}`}>{value}</div>
      <div className={styles.successStatLabel}>{label}</div>
    </div>
  );
}

/** Determinate progress bar, used during the APPLYING phase where we know
 * the denominator (rows parsed). `pct` is already clamped to 0–100. */
function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className={styles.progressBar}>
      <div
        className={styles.progressBarTrack}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={styles.progressBarFill} style={{ width: `${pct}%` }} />
      </div>
      <div className={styles.progressPercent}>{pct}%</div>
    </div>
  );
}

// Newest-first sample of row-level errors to show live, capped so a
// mostly-bad file doesn't flood the modal while the job is still running.
const MAX_LIVE_ERRORS = 4;
// On the completed view we show the full reason list (it's the final
// outcome the user acts on), scrollable, but still bound the rendered DOM
// for a pathological all-bad file.
const MAX_COMPLETED_ERRORS = 100;

/**
 * Row-level errors the worker reported, newest-first. Shared by the live
 * (capped sample) and completed (full, scrollable) views — `scroll` caps
 * the list height instead of the count so every skip reason stays visible.
 */
function RowErrors({ title, errors, max, scroll }: { title: string; errors: string[]; max: number; scroll?: boolean }) {
  if (errors.length === 0) {
    return null;
  }
  const shown = errors.slice(0, max);
  const extra = errors.length - shown.length;
  return (
    <div className={styles.recentErrors}>
      <div className={styles.recentErrorsTitle}>{title}</div>
      <ul className={`${styles.recentErrorList}${scroll ? ` ${styles.recentErrorListScroll}` : ''}`}>
        {shown.map((message, i) => (
          // Error strings aren't guaranteed unique (same message across
          // rows), so index is the only stable key available here.
          // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on a worker error string
          <li key={i} className={styles.recentErrorItem}>
            {message}
          </li>
        ))}
      </ul>
      {extra > 0 && <div className={styles.recentErrorsMore}>+{extra} more</div>}
    </div>
  );
}

export default function ImportProgress({
  phase,
  progress,
  errorMessage,
  pollError,
  fileName,
  onClose,
  onUploadAnother,
}: ImportProgressProps) {
  // `idle` is only ever a transient frame on step 3 (between advancing to
  // the view and the start mutation flipping `starting` on) — render it as
  // in-flight so the success view never flashes before tracking begins.
  const inFlight = phase === 'idle' || phase === 'starting' || phase === 'tracking';

  if (inFlight) {
    const status = progress?.status ?? null;
    const livePhase = progress?.phase ?? null;
    const rowsSeen = progress?.rowsSeen ?? null;
    const rowsProcessed = progress?.rowsProcessed ?? null;
    const rowsFailed = progress?.rowsFailed ?? null;
    const recentErrors = progress?.errors ?? [];

    // Phase-aware heading. The live `phase` is richer than `status`, so
    // prefer it; fall back to "queued" before the worker picks the job up.
    let title: string;
    if (livePhase === 'PARSING') {
      title = 'Reading your file…';
    } else if (livePhase === 'FINALIZING') {
      title = 'Finishing up…';
    } else if (status === 'QUEUED' && livePhase === null) {
      title = 'Queued for import…';
    } else {
      // APPLYING, or RUNNING with no live phase yet.
      title = 'Importing your pets…';
    }

    // Live counts sentence, matched to the phase.
    let detail: React.ReactNode;
    if (status === 'QUEUED' && livePhase === null) {
      detail = <>Your file is queued — this will begin in a moment.</>;
    } else if (livePhase === 'PARSING') {
      detail =
        rowsSeen !== null ? (
          <>
            Read {rowsSeen} row{rowsSeen === 1 ? '' : 's'} so far…
          </>
        ) : (
          <>
            Reading rows from <strong>{fileName ?? 'your file'}</strong>…
          </>
        );
    } else if (livePhase === 'APPLYING') {
      const done = rowsProcessed ?? 0;
      detail = (
        <>
          Imported {done}
          {rowsSeen !== null ? <> of {rowsSeen}</> : null} row{done === 1 && rowsSeen === null ? '' : 's'}
          {rowsFailed ? <>, {rowsFailed} skipped</> : null}…
        </>
      );
    } else if (livePhase === 'FINALIZING') {
      detail = <>Wrapping up the import…</>;
    } else {
      detail = (
        <>
          We&rsquo;re processing <strong>{fileName ?? 'your file'}</strong>. This can take a moment — keep this window
          open to watch progress, or close it and reopen Import to check back.
        </>
      );
    }

    // Determinate bar only during APPLYING, where `rowsSeen` is the full
    // parsed count and `rowsProcessed + rowsFailed` is how far we've gotten.
    // Every other phase lacks a reliable denominator → indeterminate spinner.
    const denom = livePhase === 'APPLYING' ? rowsSeen : null;
    const handled = (rowsProcessed ?? 0) + (rowsFailed ?? 0);
    const pct = denom && denom > 0 ? Math.min(100, Math.round((handled / denom) * 100)) : null;

    return (
      <div className={styles.wizardStep}>
        <div className={styles.successWrap} role="status" aria-live="polite" aria-busy="true">
          {pct !== null ? <ProgressBar pct={pct} /> : <Spinner />}
          <h3 className={styles.successTitle}>{title}</h3>
          <p className={styles.successBody}>{detail}</p>
          <RowErrors title="Recent row errors" errors={recentErrors} max={MAX_LIVE_ERRORS} />
          {pollError && <p className={styles.progressNote}>Having trouble refreshing the status — still trying…</p>}
        </div>

        <ModalActions>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalActions>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className={styles.wizardStep}>
        <div className={styles.successWrap}>
          <div className={`${styles.successHero} ${styles.successHeroError}`}>
            <ErrorCircleIcon />
          </div>
          <h3 className={styles.successTitle}>Import didn&rsquo;t finish</h3>
          <p className={styles.successBody}>
            {errorMessage ?? 'Something went wrong while importing your pets. Please try uploading again.'}
          </p>
        </div>

        <ModalActions>
          <Button variant="ghost" onClick={onUploadAnother}>
            Try again
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </ModalActions>
      </div>
    );
  }

  // Succeeded. `finalProcessed`/`finalFailed` are populated once the
  // apply phase finishes; some backend builds leave them null through
  // SUCCEEDED, in which case we fall back to the parsed `totalRows`.
  const processed = progress?.finalProcessed ?? null;
  const failed = progress?.finalFailed ?? null;
  const total = progress?.totalRows ?? null;
  const hasCounts = processed !== null || total !== null;
  // Reasons the worker skipped rows. Read off the (still-live) job by the
  // final poll, newest-first; not durably persisted, so they're shown
  // here while available rather than on a later re-open of a finished job.
  const skipReasons = progress?.errors ?? [];
  const skipTitle =
    failed && failed > 0
      ? `Why ${failed} row${failed === 1 ? ' was' : 's were'} skipped`
      : 'Why some rows were skipped';
  return (
    <div className={styles.wizardStep}>
      <div className={styles.successWrap}>
        <div className={styles.successHero}>
          <CheckIcon />
        </div>
        <h3 className={styles.successTitle}>Import complete</h3>
        <p className={styles.successBody}>
          {fileName ? (
            <>
              <strong>{fileName}</strong> has been imported.
            </>
          ) : (
            <>Your pets have been imported.</>
          )}
        </p>
        {hasCounts && (
          <div className={styles.successStats}>
            {processed !== null ? (
              <Stat value={processed} label={`pet${processed === 1 ? '' : 's'} imported`} />
            ) : (
              total !== null && <Stat value={total} label={`row${total === 1 ? '' : 's'} processed`} />
            )}
            {failed !== null && failed > 0 && (
              <Stat value={failed} label={`row${failed === 1 ? '' : 's'} skipped`} danger />
            )}
          </div>
        )}
        <RowErrors title={skipTitle} errors={skipReasons} max={MAX_COMPLETED_ERRORS} scroll />
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={onUploadAnother}>
          Upload another
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </ModalActions>
    </div>
  );
}
